// AIMS PWA - "new version available" prompt (AIMS-Offline-Inspection.md §10).
//
// When a new service-worker version is waiting, offer a reload - but NEVER
// reload automatically, and never while it would lose work:
//   - an inspection form is open  -> hold off (would drop in-progress entry)
//   - inspections are still queued -> warn, offer to sync first
// Reloading only swaps the app code (SKIP_WAITING + controllerchange). It never
// touches IndexedDB, so offline data and the outbox survive the update.

import React, { useEffect, useState, useCallback } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { activateUpdate } from '../../serviceWorkerRegistration';
import { isFormOpen } from '../../offline/reloadGuard';
import { getPendingCount, runSync } from '../../offline/sync';

export default function UpdatePrompt() {
  const [reg, setReg] = useState(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);

  // Pick up an update that arrived (custom event) or one already waiting.
  useEffect(() => {
    if (window.__AIMS_SW_WAITING__) { setReg(window.__AIMS_SW_WAITING__); setOpen(true); }
    const onUpdate = (e) => { setReg(e.detail || window.__AIMS_SW_WAITING__ || null); setOpen(true); };
    window.addEventListener('aims:sw-update', onUpdate);
    return () => window.removeEventListener('aims:sw-update', onUpdate);
  }, []);

  useEffect(() => {
    if (open) getPendingCount().then(setPending);
  }, [open]);

  const doReload = useCallback((registration) => {
    // Swap code only; controllerchange fires once the new SW takes over.
    activateUpdate(registration, () => window.location.reload());
    // Fallback if the SW had no waiting worker (already active): just reload.
    setTimeout(() => window.location.reload(), 1200);
  }, []);

  const handleReload = async () => {
    if (isFormOpen()) return; // guarded in UI too, but double-check
    if (pending > 0) {
      // Try to clear the queue first so nothing is left un-uploaded pre-reload.
      setBusy(true);
      try {
        const summary = await runSync();
        const left = await getPendingCount();
        setPending(left);
        setBusy(false);
        if (left > 0 && !(summary && summary.needsReauth)) {
          // Still pending (offline / errors) - let the user decide below.
          return;
        }
      } catch (_) { setBusy(false); return; }
    }
    doReload(reg);
  };

  const formBlocking = isFormOpen();

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 96, sm: 24 } }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: 'center', '& .MuiAlert-message': { fontSize: 12.5 } }}
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!formBlocking && (
              <Button color="inherit" size="small" onClick={handleReload} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700 }}>
                {busy ? 'Syncing…' : (pending > 0 ? 'Sync & reload' : 'Reload')}
              </Button>
            )}
            <Button color="inherit" size="small" onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
              Later
            </Button>
          </Box>
        }
      >
        {formBlocking
          ? 'A new version is ready. Finish and close the current inspection, then reload.'
          : pending > 0
            ? `A new version is ready. You have ${pending} inspection${pending === 1 ? '' : 's'} to upload first.`
            : 'A new version of AIMS is ready.'}
      </Alert>
    </Snackbar>
  );
}
